import React, { useState } from 'react';
import { Ticket, Euro, Loader2, ShoppingCart, Mail } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from './dialog';
import { Button } from './button';
import { Input } from './input';
import { PRICES } from '../config/prices';

const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';

// Preset cards describe what the voucher would unlock in plain language —
// a recipient who got the code as a gift shouldn't have to know that "L"
// means anything specific.
const PRESET_AMOUNTS_CENTS = [
  { label: 'For a Size M image', sub: '~40x30 cm', cents: Math.round((PRICES.M || 2.49) * 100) },
  { label: 'For a Size L image', sub: '~80x60 cm', cents: Math.round((PRICES.L || 4.49) * 100) },
];

const MIN_CUSTOM_AMOUNT_CENTS = 500;

const PublicBuyVoucherDialog = ({ open, onOpenChange }) => {
  const [email, setEmail] = useState('');
  // Explicit mode toggle so clicking the custom card switches selection
  // even if the user hasn't changed the amount yet — relying on
  // `customAmountInput` truthiness alone meant the card looked deselected
  // until the user typed a digit.
  const [mode, setMode] = useState('preset'); // 'preset' | 'custom'
  const [selectedAmountCents, setSelectedAmountCents] = useState(PRESET_AMOUNTS_CENTS[1].cents);
  const [customAmountInput, setCustomAmountInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    setError(null);
    let amount = selectedAmountCents;
    const isCustom = mode === 'custom';
    if (isCustom) {
      const parsed = Math.round(parseFloat(customAmountInput) * 100);
      if (Number.isFinite(parsed) && parsed > 0) {
        amount = parsed;
      } else {
        amount = 0;
      }
    }
    if (!amount || amount <= 0) {
      setError('Pick a preset or enter a custom amount.');
      return;
    }
    if (isCustom && amount < MIN_CUSTOM_AMOUNT_CENTS) {
      setError('Custom amounts start at €5.00.');
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setError('Enter a valid email — your voucher code is delivered there.');
      return;
    }
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/public/buy-voucher`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount_cents: amount, email: email.trim() }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Failed to start checkout');
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
        return;
      }
      throw new Error('Checkout URL missing');
    } catch (e) {
      setError(e.message || 'Failed to start checkout');
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white sm:rounded-2xl p-0 overflow-hidden max-w-md">
        <div className="bg-gradient-to-br from-gray-900 to-gray-700 text-white px-6 py-5 flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-white/15 flex items-center justify-center">
            <Ticket className="h-5 w-5" />
          </div>
          <div>
            <DialogTitle className="text-lg font-semibold">Buy a voucher</DialogTitle>
            <p className="text-xs text-gray-300 mt-0.5">
              Pay once, redeem anytime. Codes can be shared with friends.
            </p>
          </div>
        </div>

        <div className="px-6 py-5 space-y-4">
          <section className="space-y-2">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              <Mail className="h-3.5 w-3.5" /> Email
            </div>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              className="w-full"
            />
            <p className="text-[11px] text-gray-500 text-center">
              Your voucher code is shown on-screen after payment and emailed to you.
            </p>
          </section>

          <section className="space-y-2">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              <ShoppingCart className="h-3.5 w-3.5" /> Amount
            </div>
            <div className="space-y-2">
              {PRESET_AMOUNTS_CENTS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => { setMode('preset'); setSelectedAmountCents(p.cents); }}
                  className={`w-full p-3 rounded-lg border text-sm transition-all flex items-center justify-between gap-2 ${mode === 'preset' && selectedAmountCents === p.cents
                    ? 'border-gray-900 bg-gray-100 font-semibold text-gray-900 shadow-sm'
                    : 'border-gray-200 hover:bg-gray-50'
                    }`}
                >
                  <div className="text-left">
                    <div>{p.label}</div>
                    <div className="text-[11px] text-gray-500">{p.sub}</div>
                  </div>
                  <div className="font-semibold">€{(p.cents / 100).toFixed(2)}</div>
                </button>
              ))}
              {/* Click anywhere on the card switches to custom mode (even if
                  the input is still empty); the inner input updates the
                  amount once the user types. */}
              <div
                role="button"
                tabIndex={0}
                onClick={() => setMode('custom')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setMode('custom'); } }}
                className={`block w-full p-3 rounded-lg border text-sm transition-all cursor-pointer ${mode === 'custom'
                  ? 'border-gray-900 bg-gray-100 shadow-sm'
                  : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div>Custom amount</div>
                    <div className="text-[11px] text-gray-500">Minimum €5</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Euro className="h-3.5 w-3.5 text-gray-400" />
                    <Input
                      type="number"
                      min="5"
                      step="0.01"
                      placeholder="5.00"
                      value={customAmountInput}
                      onChange={(e) => setCustomAmountInput(e.target.value)}
                      onFocus={() => setMode('custom')}
                      onClick={(e) => e.stopPropagation()}
                      className="w-20 border-0 p-0 h-auto bg-transparent text-sm font-semibold text-right focus-visible:ring-0 focus-visible:ring-offset-0"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {error && <p className="text-xs text-red-500 text-center">{error}</p>}

          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full bg-gray-900 hover:bg-gray-700 text-white border-0 shadow-md text-sm font-semibold h-11"
          >
            {loading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirecting…</>
            ) : (
              <><ShoppingCart className="h-4 w-4 mr-2" /> Continue to payment</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PublicBuyVoucherDialog;
